import { intlLocale, t } from '@/lib/i18n';

const rtf = new Intl.RelativeTimeFormat(intlLocale, { numeric: 'auto', style: 'short' });
/** "3 min. ago" → "3 min ago" (no abbreviation dots). */
const rel = (v: number, unit: Intl.RelativeTimeFormatUnit) => rtf.format(v, unit).replace(/\.(?=\s|$)/g, '');
const absFmt = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium', timeStyle: 'short' });
const absFmtSeconds = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium', timeStyle: 'medium' });
const timeFmt = new Intl.DateTimeFormat(intlLocale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatRelative(value: string | Date | null | undefined, now = Date.now()): string {
  const d = toDate(value);
  if (!d) return '—';
  const diffSec = Math.round((d.getTime() - now) / 1000);
  const abs = Math.abs(diffSec);
  // Small positive offsets are clock skew, not real future times.
  if (abs < 45 || (diffSec > 0 && diffSec < 120)) return t('just now');
  if (abs < 3600) return rel(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rel(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 30) return rel(Math.round(diffSec / 86400), 'day');
  if (abs < 86400 * 365) return rel(Math.round(diffSec / (86400 * 30)), 'month');
  return rel(Math.round(diffSec / (86400 * 365)), 'year');
}

export function formatAbsolute(value: string | Date | null | undefined, withSeconds = false): string {
  const d = toDate(value);
  if (!d) return '—';
  return (withSeconds ? absFmtSeconds : absFmt).format(d);
}

export function formatTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d ? timeFmt.format(d) : '—';
}

const dayFmt = new Intl.DateTimeFormat(intlLocale, { weekday: 'long', day: 'numeric', month: 'long' });
const dayFmtYear = new Intl.DateTimeFormat(intlLocale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/** "Sunday, 25 October" — the year only when it isn't this one. */
export function formatDay(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return (d.getFullYear() === new Date().getFullYear() ? dayFmt : dayFmtYear).format(d);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toLocaleString(intlLocale, { maximumFractionDigits: v < 10 ? 1 : 0 })} ${units[i]}`;
}

export function formatDuration(from: string | null | undefined, to?: string | null): string {
  const a = toDate(from);
  if (!a) return '—';
  const b = toDate(to) ?? new Date();
  let s = Math.max(0, Math.round((b.getTime() - a.getTime()) / 1000));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  if (h) return t('{h} hr {m} min', { h, m });
  if (m) return t('{m} min {s} sec', { m, s });
  return t('{s} sec', { s });
}

export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '—';
}

export function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function isEmptyObject(value: unknown): boolean {
  return !value || (typeof value === 'object' && Object.keys(value as object).length === 0);
}

/** Duration phrase after "Connected …": "for 5 min", "for 3 hours", "since yesterday", "for 4 days". Null for < 1 minute. */
export function formatSince(value: string | Date | null | undefined, now = Date.now()): string | null {
  const d = toDate(value);
  if (!d) return null;
  const sec = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (sec < 60) return null;
  if (sec < 3600) return t('for {n} min', { n: Math.round(sec / 60) });
  if (sec < 86400) {
    const h = Math.round(sec / 3600);
    return h === 1 ? t('for an hour') : t('for {n} hours', { n: h });
  }
  const days = Math.round(sec / 86400);
  if (days === 1) return t('since yesterday');
  if (days < 31) return t('for {n} days', { n: days });
  const months = Math.round(days / 30);
  if (months < 12) return months === 1 ? t('for a month') : t('for {n} months', { n: months });
  const years = Math.round(days / 365);
  return years === 1 ? t('for a year') : t('for {n} years', { n: years });
}
